import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useStyles } from "../../theme/appStyles";
import {
  useDeleteCustomerTransactionMutation,
  useGetCustomerQuery,
  useGetCustomerTransactionQuery,
} from "../../Api/attoDeskApi";
import {
  ICustomer,
  ICustomerTransaction,
} from "../../Api/Interface/api.interface";
import NewPopUpCustomer from "../Customer/Components/NewPopUpCustomer";
import NewCustomerTransactionPopUp from "./Components/NewCustomerTransactionPopUp";
import { useNotifier } from "../../Core/Notifier";
import DeletePopup from "../../Components/Delete/DeletePopup";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { CiSearch } from "react-icons/ci";
import useDebounce from "../../Components/SearchDebonce/useDebounce";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const { id: customerId } = useParams<{ id: string }>();
  const [selectCustomer, setSelectCustomer] = useState<ICustomer | null>(null);
  const [transactionData, setTransactionData] =
    useState<ICustomerTransaction>();
  const [deleteCategory] = useDeleteCustomerTransactionMutation();
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddTransaction, setOpenAddTransaction] = useState(false);
  const { showErrorMessage, showMessage } = useNotifier();
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] =
    useState<ICustomerTransaction | null>(null);
  useEffect(() => {
    // console.log("URL Credit ID:", customerId);
  }, [customerId]);

  const { data, isLoading, isError } = useGetCustomerQuery({
    searchText: "",
    id: String(customerId),
  });

  const {
    data: customerTransactionData,
    isLoading: customerDataIsLoading,
    refetch,
  } = useGetCustomerTransactionQuery({
    searchText: currentSearchQuery,
    customerId: String(customerId),
  });

  const customer = useMemo(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) {
      return data.data[0] as ICustomer;
    }
    return null;
  }, [data]);

  const transactions: ICustomerTransaction[] = useMemo(() => {
    if (
      customerTransactionData &&
      Array.isArray(customerTransactionData.data)
    ) {
      return customerTransactionData.data as ICustomerTransaction[];
    }
    return [];
  }, [customerTransactionData]);

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleOpenDialog = (customer: ICustomer) => {
    setSelectCustomer(customer);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectCustomer(null);
    setOpenDialog(false);
  };

  const handleOpenAddTransaction = () => {
    setTransactionData(undefined);
    setOpenAddTransaction(true);
  };

  const handleCloseAddTransaction = () => {
    setTransactionData(undefined);
    setOpenAddTransaction(false);
  };

  if (isLoading || customerDataIsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error">
        Error fetching customer data. Please try again later.
      </Typography>
    );
  }

  if (!customer) {
    return <Typography>No customer data available.</Typography>;
  }
  const handleOpenDeletePopup = (coursing: ICustomerTransaction) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage("Deleted successfully");
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage("Failed to delete the customer");
      }
    } catch (error) {
      showErrorMessage("Failed to delete the customer");
    }
  };

  const gridHeight = isMobile ? "30px" : "400px";

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.stepperCard}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={11}
                md={11}
                sm={11}
                xs={11}
                pt={2}
                alignContent={"center"}
              >
                <Typography variant="h2">
                  {customer.customerFirstName + " " + customer.customerLastName}
                </Typography>
              </Grid>
              <Grid
                item
                lg={1}
                md={1}
                sm={1}
                xs={1}
                pt={2}
                alignContent={"center"}
              >
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardIosIcon />}
                  className={classes.addButton}
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={3} md={3} sm={12} xs={12}>
          <Grid container spacing={2}>
            <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
              <Card className={classes.stepperCard}>
                <Grid container spacing={2}>
                  <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Customer Details
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, px: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                        direction={"column"}
                      >
                        <Typography variant="subtitle1">
                          <span className={classes.customerDetails}>
                            Phone:
                          </span>{" "}
                          {customer.customerMobileNum}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, px: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                        direction={"column"}
                      >
                        <Typography variant="subtitle1">
                          <span className={classes.customerDetails}>
                            E-mail:
                          </span>{" "}
                          {customer.customerEmail?.toLowerCase()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, px: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                        direction={"column"}
                      >
                        <Typography variant="subtitle1">
                          <span className={classes.customerDetails}>
                            Phone:
                          </span>{" "}
                          {customer.customerMobileNum}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, px: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                        direction={"column"}
                      >
                        <Typography variant="subtitle1">
                          <span className={classes.customerDetails}>
                            Address:
                          </span>{" "}
                          {customer.customerAddress}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
              <Card className={classes.stepperCard}>
                <Grid container spacing={2}>
                  <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Description
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, px: 2 }}
                      className={classes.titleGrid}
                      minHeight={"400px"}
                    >
                      <Grid item lg={12} md={12} sm={12} xs={12} pt={2}>
                        <Typography variant="subtitle1">
                          {customer.customerDescription}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={9} md={9} sm={12} xs={12}>
          <Card className={classes.card}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={4}
                md={4}
                sm={4}
                xs={4}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>
                  Customer Transaction
                </Typography>
              </Grid>
              <Grid
                item
                lg={8}
                md={8}
                sm={8}
                xs={8}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Grid
                  container
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ display: "flex" }}
                >
                  <Grid item>
                    <TextField
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CiSearch className={classes.searchWidgetIcon} />
                          </InputAdornment>
                        ),
                        classes: {
                          root: classes.formControlList,
                        },
                      }}
                      fullWidth
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Name .."
                      sx={{ width: "100%" }}
                      size="small"
                    />
                  </Grid>

                  <Grid item>
                    <Button
                      variant="contained"
                      className={classes.addButton}
                      onClick={handleOpenAddTransaction}
                    >
                      Add Transaction
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid>
              <TableContainer>
                <Table className={classes.table}>
                  <TableHead className={classes.tableHeaderRow}>
                    <TableRow>
                      <TableCell className={classes.tablheader}>
                        <strong>Transaction Name</strong>
                      </TableCell>
                      <TableCell className={classes.tablheader}>
                        <strong>Amount</strong>
                      </TableCell>
                      <TableCell className={classes.tablheader}>
                        <strong>Collection</strong>
                      </TableCell>
                      <TableCell className={classes.tablheader}>
                        <strong>Reminder</strong>
                      </TableCell>
                      <TableCell className={classes.tablheader}>
                        <strong>Description</strong>
                      </TableCell>
                      <TableCell className={classes.tablheader} sx={{textAlign: "right"}}>
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow
                        key={transaction.id}
                        className={classes.tableRowHover}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          className={classes.tablCell}
                        >
                          {transaction.transactionName}
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {transaction.transactionAmount}
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {transaction.transactionCollection}
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {transaction.transactionReminderDate}
                        </TableCell>
                        <TableCell scope="row" className={classes.tablCell}>
                          {transaction.transactionDescription}
                        </TableCell>
                        <TableCell className={classes.tablCell}>
                          <Grid
                            container
                            spacing={1}
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <Grid item>
                              <Button
                                variant="contained"
                                className={classes.editButtonList}
                                onClick={() => {
                                  setTransactionData(transaction);
                                  setOpenAddTransaction(true);
                                }}
                              >
                                <EditSharpIcon className={classes.editListIcon} />
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button
                                variant="contained"
                                className={classes.deleteButtonList}
                                onClick={() =>
                                  handleOpenDeletePopup(transaction)
                                }
                              >
                                <DeleteOutlineSharpIcon
                                className={classes.deleteListIcon}
                              />
                              </Button>
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {selectCustomer && (
                <NewPopUpCustomer
                  openModel={openDialog}
                  handleCloseDialog={handleCloseDialog}
                  data={selectCustomer}
                />
              )}
              {openAddTransaction && (
                <NewCustomerTransactionPopUp
                  openModel={openAddTransaction}
                  handleCloseDialog={handleCloseAddTransaction}
                  data={transactionData}
                  customerId={customerId}
                />
              )}
              {coursingToDelete && (
                <DeletePopup
                  open={openDeleteCategory}
                  handleCloseDelete={handleCloseDeletePopup}
                  onConfirm={async () => {
                    await handleDelete(coursingToDelete.id.toString());
                  }}
                  title="Delete Customer"
                  content={`Are you sure you want to delete "${coursingToDelete.transactionName}"?`}
                />
              )}
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
